import { useDispatch } from 'react-redux';
import {
  setCustomIntegrations,
  setIncludeSandboxes,
  setSlackIntegrationFullStatus,
  setTeamsIntegrationStatus,
  SlackConnectionStatus,
  TeamsConnectionStatus,
} from '../reducers/integrationsReducer';
import { useDataIntegrationApiFacade } from '../apis/facades/useDataIntegrationApiFacade';
import { useCallback } from 'react';
import { setIsSlackRecipientsCached, setSlackRecipients } from '../reducers/slackReducer';
import { telemetry } from '../telemetry';
import { useSweepNotifications } from '../components/notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../reducers/notificationsReducer';
import { CustomWebhookInterface } from '../components/dashboard/webhooks/WebhooksContext';
import { sendBiEvent } from '../services/bi';
import { ACTIONS_EVENTS } from '../services/events';

const useIntegrations = () => {
  const dispatch = useDispatch();
  const {
    get_slack_status,
    get_slack_recipients,
    patch_slackIncludeSandboxes,
    get_custom_integrations,
    post_custom_integration,
    put_custom_integration,
    delete_custom_integration,
    get_custom_integration_password,
    get_teams_status,
    disconnect_teams,
    get_teams_recipients,
    get_teams_channels,
    get_teams_recipient_labels_For_Automation,
  } = useDataIntegrationApiFacade();
  const { addNotification } = useSweepNotifications();

  const notifyUser = useCallback(
    ({
      variant,
      message,
      keepOpen = false,
    }: {
      variant: SweepNotificationVariant;
      message: string;
      keepOpen?: boolean;
    }) => {
      addNotification({
        message: message,
        keepOpen,
        variant,
      });
    },
    [addNotification],
  );

  const getSlackStatus = async () => {
    const response = await get_slack_status();
    const status = response.status as SlackConnectionStatus;
    const upgradeRequired = !!response.upgradeRequired;
    const includeSandboxes = response.includeSandboxes;
    dispatch(
      setSlackIntegrationFullStatus({
        slackIntegrationStatus: status,
        upgradeRequired,
        includeSandboxes,
      }),
    );
    return status;
  };

  const getSlackRecipients = useCallback(
    async (idsArray?: string[], invalidateCache?: boolean) => {
      const response = await get_slack_recipients(idsArray, invalidateCache);
      dispatch(setIsSlackRecipientsCached({ isCached: response.isCached }));
      dispatch(setSlackRecipients({ slackRecipients: response.data }));
      return response.data;
    },
    [dispatch, get_slack_recipients],
  );

  const updateIncludeSandboxes = useCallback(
    async (includeSandboxes: boolean) => {
      //supporting happy state
      dispatch(setIncludeSandboxes({ includeSandboxes }));
      const connectedMsg = 'Slack alerts can now be triggered from sandbox environments';
      const disconnectedMsg = 'Sandbox environments will no longer trigger Slack alerts';

      try {
        await patch_slackIncludeSandboxes(includeSandboxes);
        notifyUser({
          variant: includeSandboxes
            ? SweepNotificationVariant.Success
            : SweepNotificationVariant.Warning,
          message: includeSandboxes ? connectedMsg : disconnectedMsg,
        });
      } catch (error) {
        telemetry.captureError(error);
        dispatch(setIncludeSandboxes({ includeSandboxes: !includeSandboxes }));
        notifyUser({
          variant: SweepNotificationVariant.Error,
          message: 'Connection failed, please try again later',
          keepOpen: true,
        });
      }
    },
    [patch_slackIncludeSandboxes, dispatch, notifyUser],
  );

  const getCustomIntegrations = useCallback(async (): Promise<CustomWebhookInterface[]> => {
    const integrations = await get_custom_integrations();
    dispatch(setCustomIntegrations(integrations));
    return integrations;
  }, [dispatch, get_custom_integrations]);

  const createCustomIntegration = useCallback(
    async (newWebhook: CustomWebhookInterface): Promise<CustomWebhookInterface> => {
      const integration = await post_custom_integration(newWebhook);
      sendBiEvent({
        name: ACTIONS_EVENTS.customIntegrationAdd,
      });
      await getCustomIntegrations();
      return integration;
    },
    [getCustomIntegrations, post_custom_integration],
  );

  const updateCustomIntegration = useCallback(
    async (webhook: CustomWebhookInterface): Promise<CustomWebhookInterface> => {
      const integration = await put_custom_integration(webhook.id, webhook);
      await getCustomIntegrations();
      return integration;
    },
    [getCustomIntegrations, put_custom_integration],
  );

  const deleteCustomIntegration = async (webhookId: string, dryRun?: boolean): Promise<void> => {
    await delete_custom_integration(webhookId, dryRun);
    await getCustomIntegrations();
  };

  const getCustomIntegrationPassword = useCallback(
    async (webhookId: string): Promise<string> => {
      const password = await get_custom_integration_password(webhookId);
      return password;
    },
    [get_custom_integration_password],
  );

  const getTeamsStatus = async () => {
    const status = await get_teams_status();
    dispatch(setTeamsIntegrationStatus(status));
    return status;
  };

  const disconnectTeams = async () => {
    dispatch(setTeamsIntegrationStatus(TeamsConnectionStatus.Initialization));
    await disconnect_teams();
  };

  const getTeamsRecipients = useCallback(async () => {
    const recipients = await get_teams_recipients();
    return recipients;
  }, [get_teams_recipients]);

  const getTeamsChannels = useCallback(
    async (teamId: string) => {
      const channels = await get_teams_channels(teamId);
      return channels;
    },
    [get_teams_channels],
  );
  const getTeamsRecipientLabelsForAutomation = useCallback(
    async (recipientIds: string[]) => {
      const labels = await get_teams_recipient_labels_For_Automation(recipientIds);
      return labels;
    },
    [get_teams_recipient_labels_For_Automation],
  );

  return {
    getSlackStatus,
    getSlackRecipients,
    updateIncludeSandboxes,
    getCustomIntegrations,
    createCustomIntegration,
    updateCustomIntegration,
    deleteCustomIntegration,
    getCustomIntegrationPassword,
    getTeamsStatus,
    disconnectTeams,
    getTeamsRecipients,
    getTeamsChannels,
    getTeamsRecipientLabelsForAutomation,
  };
};

export { useIntegrations };
